import { Button } from '@pypestream/design-system';
import { FC } from 'react';

import {
  SETTING_LABEL,
  formatSetting,
} from '../../pages/agent-settings/agent-settings-model';
import type { AssistantSettingChange } from './solution-assistant-model';

import './setting-change-card.scss';

const AGENT = { supervisor: 'Supervisor Agent', knowledge: 'Knowledge Agent' };

/**
 * The assistant's proposed agent-setting changes, before any of them is made:
 * each setting, what it is now, what it would become. Apply saves the ticked
 * ones as one change; Undo puts them back.
 */
export const SettingChangeCard: FC<{
  changes: AssistantSettingChange[];
  status?: 'applied' | 'discarded';
  busy?: boolean;
  readOnly?: boolean;
  onToggle: (index: number) => void;
  onApply: () => void;
  onDiscard: () => void;
  onUndo: () => void;
}> = ({
  changes,
  status,
  busy,
  readOnly,
  onToggle,
  onApply,
  onDiscard,
  onUndo,
}) => {
  const on = changes.filter((c) => !c.off).length;
  const label = (key: string) =>
    SETTING_LABEL[key as keyof typeof SETTING_LABEL] ?? key;
  return (
    <div
      className={`c-setting-changes${status ? ` is-${status}` : ''}`}
      data-testid="ps-solution-assistant-setting-changes"
    >
      <div className="c-setting-changes__head">
        <span>Proposed changes</span>
        <span>
          {changes.length} setting{changes.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="c-setting-changes__list">
        {changes.map((c, i) => (
          <li key={`${c.agent}.${c.key}`} className="c-setting-changes__row">
            <input
              type="checkbox"
              checked={!c.off}
              disabled={!!status || readOnly}
              aria-label={`Include ${label(c.key)}`}
              onChange={() => onToggle(i)}
            />
            <span>
              <span className="c-setting-changes__where">{AGENT[c.agent]}</span>
              <span className="c-setting-changes__what">{label(c.key)}</span>
              <span className="c-setting-changes__from">
                {formatSetting(c.key, c.from)}
              </span>{' '}
              →{' '}
              <span className="c-setting-changes__to">
                {formatSetting(c.key, c.to)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      {status === 'applied' ? (
        <div className="c-setting-changes__done">
          <span>
            Applied {on} change{on === 1 ? '' : 's'}
          </span>
          {!readOnly && (
            <button
              type="button"
              className="c-setting-changes__undo"
              disabled={busy}
              onClick={onUndo}
            >
              Undo
            </button>
          )}
        </div>
      ) : status === 'discarded' ? (
        <div className="c-setting-changes__done">
          <span>Discarded</span>
        </div>
      ) : (
        !readOnly && (
          <div className="c-setting-changes__acts">
            <Button
              size="small"
              variant="ghost"
              disabled={busy}
              onClick={onDiscard}
            >
              Discard
            </Button>
            <Button
              size="small"
              disabled={busy || on === 0}
              test-id="ps-solution-assistant-apply-settings"
              onClick={onApply}
            >
              {busy
                ? 'Applying…'
                : on === changes.length
                  ? on === 1
                    ? 'Apply change'
                    : `Apply all ${on}`
                  : `Apply ${on} of ${changes.length}`}
            </Button>
          </div>
        )
      )}
    </div>
  );
};

/** A question the assistant asks instead of guessing; an option answers it. */
export const AssistantAskCard: FC<{
  question: string;
  why: string;
  options: string[];
  disabled?: boolean;
  onPick: (option: string) => void;
}> = ({ question, why, options, disabled, onPick }) => (
  <div className="c-assistant-ask" role="group" aria-label={question}>
    <p className="c-assistant-ask__q">{question}</p>
    {why && <p className="c-assistant-ask__why">{why}</p>}
    <div className="c-assistant-ask__opts">
      {options.map((o, i) => (
        <button
          key={o}
          type="button"
          className="c-assistant-ask__opt"
          disabled={disabled}
          onClick={() => onPick(o)}
        >
          <span className="c-assistant-ask__num">{i + 1}</span>
          <span>{o}</span>
          {i === 0 && <span className="c-assistant-ask__rec">Recommended</span>}
        </button>
      ))}
    </div>
  </div>
);
